import { Browser } from '@capacitor/browser';
import { consumeLoginReturnPath, storeAuthTokens } from 'utils/auth';
import { fireLoginRisk } from 'utils/loginRisk';
import { fetchGameCloseMaintenance } from 'utils/maintenance';
import { APP_DOMAIN } from 'utils/urls';

import {
  disableBiometric,
  isBiometricEnrolled,
  refreshStoredToken,
  unlockBiometric
} from './nativeBiometric';
import { ExchangedSession, exchangeRefreshToken, isAccountStatusOk } from './nativeLogin';

/**
 * Native login entry point (EN-5415).
 *
 * Tries a biometric unlock first and only opens the auth app in the system
 * browser when that isn't possible. Every failure path either falls back to
 * the browser or surfaces a card that offers it, so the player can always
 * reach the password form (AC6) — but is never dead-ended OR silently
 * redirected.
 *
 * AC13 (QA blocker — Noe 2026-08-17, re-tested 8/18): a failed or cancelled
 * scan used to open the auth app in the browser, which read as "kicked to the
 * Login/SignUp page", and the hard-error path disabled the enrollment on the
 * way. Now a scan that doesn't complete keeps the player exactly where they
 * were: this module publishes the outcome to `subscribeUnlockPrompt` and the
 * App-level `BiometricUnlockPrompt` raises the matching card (retry / locked
 * out / turned off). The browser opens only when the player asks for the
 * password, or when there is genuinely nothing to retry.
 *
 * Imported dynamically by redirectToLogin to avoid a static import cycle
 * (utils/auth -> here -> utils/auth).
 */

const PASSWORD_LOGIN_URL = () => `https://auth.${APP_DOMAIN}/loginapp?callback=native`;

/** The player's escape hatch — exported so the unlock cards can offer it. */
export function openPasswordLogin(): void {
  Browser.open({ url: PASSWORD_LOGIN_URL() }).catch(() => {});
}

/**
 * How a native login attempt ended, for the UI layer.
 *
 *  - 'entered'          — logged in; the page is already navigating.
 *  - 'password-handoff' — the browser was opened (not enrolled, maintenance,
 *                         account-status routing). Nothing for the UI to add.
 *  - 'not-completed'    — scan failed or was cancelled. Enrollment intact.
 *                         Show the retry card (AC13).
 *  - 'locked-out'       — OS lockout. Enrollment intact. Show the locked card
 *                         (AC12): passcode-unlock clears it, password works now.
 *  - 'turned-off'       — the stored credential no longer works (token dead,
 *                         keystore unreadable, biometrics changed — AC9). The
 *                         enrollment has been disabled; say so instead of
 *                         silently bouncing to a login page.
 *  - 'connection-failed'— the exchange never got a response after every retry
 *                         (see exchangeRefreshToken's 'network' reason). The
 *                         credential itself was never actually checked, so
 *                         enrollment stays intact — offer another method now,
 *                         but biometrics is still there to try next time.
 */
export type NativeLoginOutcome =
  | 'entered'
  | 'password-handoff'
  | 'not-completed'
  | 'locked-out'
  | 'turned-off'
  | 'connection-failed';

type UnlockPromptState = Extract<
  NativeLoginOutcome,
  'not-completed' | 'locked-out' | 'turned-off' | 'connection-failed'
>;

/**
 * Single App-level subscriber (BiometricUnlockPrompt). A registry rather than
 * props because startNativeLogin is reached from five different tap targets
 * (GuestCtaButton, JoinButton, LoginRequiredModal, TournamentDetailModal,
 * Lobby) via a fire-and-forget util — threading state up through all of them
 * would touch every caller to solve a problem none of them own.
 */
let promptListener: ((state: UnlockPromptState) => void) | null = null;

export function subscribeUnlockPrompt(cb: (state: UnlockPromptState) => void): () => void {
  promptListener = cb;
  return () => {
    if (promptListener === cb) promptListener = null;
  };
}

/**
 * Raise the card for a non-final outcome — with the browser as the fallback
 * if no prompt is mounted, because "stay logged out with no way forward" is
 * the one result AC6 forbids.
 */
function raise(state: UnlockPromptState): UnlockPromptState | 'password-handoff' {
  if (promptListener) {
    promptListener(state);
    return state;
  }
  openPasswordLogin();
  return 'password-handoff';
}

/** Where exchangeRefreshToken has gotten to, for the "Reconnecting…" toast. */
export type ExchangeRetryStatus = { attempt: number; maxAttempts: number } | null;

/**
 * Separate registry from subscribeUnlockPrompt on purpose: this is a transient
 * in-progress status (BiometricUnlockPrompt renders it as a small toast, not a
 * card), not one of the terminal outcomes that raise() hands out. QA asked for
 * a retry that's visible rather than one that only shows up in the console —
 * this is what makes it visible.
 */
let retryListener: ((status: ExchangeRetryStatus) => void) | null = null;

export function subscribeExchangeRetry(cb: (status: ExchangeRetryStatus) => void): () => void {
  retryListener = cb;
  return () => {
    if (retryListener === cb) retryListener = null;
  };
}

export async function startNativeLogin(): Promise<NativeLoginOutcome> {
  if (!isBiometricEnrolled()) {
    openPasswordLogin();
    return 'password-handoff';
  }

  const unlock = await unlockBiometric();
  // AC13: neither of these clears the enrollment, and neither leaves the app.
  if (unlock.status === 'not-completed') return raise('not-completed');
  if (unlock.status === 'locked-out') return raise('locked-out');
  // The credential is gone and unlockBiometric already cleared the enrollment.
  if (unlock.status !== 'ok') return raise('turned-off');

  const exchanged = await exchangeRefreshToken(unlock.refreshToken, (attempt, maxAttempts) =>
    retryListener?.({ attempt, maxAttempts })
  );
  // Whatever happens next (success, or one of the raise() cards) replaces
  // whatever the toast was showing — clear it so a stale "Reconnecting…"
  // never lingers underneath.
  retryListener?.(null);
  if (!exchanged.ok) {
    if (exchanged.reason === 'network') {
      // Every attempt failed to even get a response — the credential itself
      // was never checked, so there is nothing to disable. Offer another way
      // in now; biometrics is still enrolled for the next attempt.
      return raise('connection-failed');
    }
    // Descope looked at the token and said no — expired, revoked, or rotated
    // out from under us. Drop the enrollment so the next attempt doesn't
    // repeat a prompt that can't succeed (AC9) — and SAY so, rather than
    // dumping the player on a login page with the setting mysteriously off.
    await disableBiometric();
    return raise('turned-off');
  }
  const session: ExchangedSession = exchanged;

  // Descope rotated the token during the exchange, so the keystore copy is now
  // stale from this point on, regardless of which path below returns.
  // Re-arm BEFORE the account-status gate, not after: that gate can hand off
  // to the auth app (e.g. for re-verification) and return without ever
  // reaching completion. Left un-rearmed on that path, the keystore still
  // holds the dead pre-exchange token — the NEXT biometric attempt (after the
  // player finishes re-verifying in the auth app) would silently fail against
  // Descope and only then disable the enrollment (AC9): a scan that "worked"
  // followed by one that mysteriously didn't, right after finishing what they
  // were routed to do.
  const rearmed = await refreshStoredToken(unlock.email, session.refreshJwt);
  if (!rearmed.ok) {
    // Can't re-arm at all — the keystore would be left holding the dead
    // pre-exchange token no matter which path below returns. Disable now so
    // the next attempt goes straight to password instead of through a doomed
    // scan; this session still completes normally below.
    console.warn('[Biometrics] failed to re-arm stored credential after login:', rearmed.error);
    await disableBiometric();
  }

  // Account status can change after enrollment (denied, duplicate, re-verify).
  // The auth page owns that routing, so hand off rather than fast-pathing in.
  if (!(await isAccountStatusOk(unlock.email, session.sessionJwt))) {
    console.log('[Biometrics] account status needs routing, deferring to auth app');
    openPasswordLogin();
    return 'password-handoff';
  }

  // Same gate the deep-link path applies (Unity parity). The auth app renders
  // its own maintenance screen, so hand off instead of entering the lobby.
  const { active } = await fetchGameCloseMaintenance(session.sessionJwt);
  if (active) {
    openPasswordLogin();
    return 'password-handoff';
  }

  storeAuthTokens(session.sessionJwt, session.refreshJwt);

  // Keep biometric sign-ins visible to fraud monitoring. The auth page posts
  // this for every login it handles; this path never touches it, so it has to
  // post its own. Fire-and-forget, and after storeAuthTokens so getAccessToken()
  // can supply the bearer.
  fireLoginRisk(session.sessionJwt);

  window.location.replace(window.location.origin + consumeLoginReturnPath());
  return 'entered';
}
